import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export interface FounderOpenPosition {
  tradeId: string;
  underlying: string;
  strategy: string;
  direction?: string;
  sellStrike?: number | null;
  buyStrike?: number | null;
  spreadWidth?: number | null;
  expiration?: string;
  dte?: number | null;
  credit: number;
  quantity?: number;
  capitalAtRisk?: number | null;
  unrealizedPnl?: number | null;
  status: string;
}

export interface FounderClosedTrade {
  tradeId: string;
  underlying: string;
  strategy: string;
  expiration?: string;
  quantity?: number;
  credit: number;
  filledCredit: number;
  exitPrice?: number | null;
  realizedPnl: number;
  capitalAtRisk?: number | null;
  returnPct?: number | null;
  exitReason?: string;
  entryAt?: string;
  exitAt?: string;
}

export interface FounderTradesResponse {
  openPositions: FounderOpenPosition[];
  recentClosed: FounderClosedTrade[];
  summary: {
    openCount: number;
    monthRealizedPnl: number;
    ytdRealizedPnl: number;
    monthClosedCount: number;
    ytdClosedCount: number;
    monthReturnPct?: number | null;
    ytdReturnPct?: number | null;
  };
  pnlBasis?: string;
  historyDays?: number;
  asOf: string | null;
}

export interface FounderConnection {
  connectionId: string;
  label: string;
  accountNumber: string;
  accountType?: string | null;
}

export interface FounderConfigResponse {
  connections: FounderConnection[];
  current: { accountNumber: string; label: string | null } | null;
}

export const boringBucketService = {
  /** Founder's real-money options trades — gated by Boring Bucket membership. */
  async getFounderTrades(token: string): Promise<FounderTradesResponse> {
    const res = await api.get('/boring-bucket/founder-trades', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  /**
   * Force a fresh sync against TastyTrade. Mirrors the options-trader
   * reconcile pattern (separate POST endpoint). Admin-only on the server.
   * Caller should refetch getFounderTrades() after this resolves.
   */
  async reconcileFounderTrades(token: string): Promise<{ ok: boolean; sync?: any }> {
    const res = await api.post('/boring-bucket/founder-trades/reconcile', {}, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000,
    });
    return res.data;
  },

  /**
   * Start the Boring Bucket membership checkout. Requires the one-time signup
   * token from the approval email; the backend validates it before creating
   * the Stripe session.
   */
  async startCheckout(
    token: string,
    signupToken: string,
    billing: 'monthly' | 'yearly',
  ): Promise<{ url: string }> {
    const res = await api.post(
      '/boring-bucket/checkout',
      { token: signupToken, billing },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  },

  /**
   * DiscourseConnect SSO — exchange Discourse's signed sso/sig challenge for a
   * signed return URL. The backend gates on Boring Bucket membership.
   */
  async discourseSso(
    token: string,
    sso: string,
    sig: string,
  ): Promise<{ redirectUrl: string }> {
    const res = await api.post(
      '/discourse-sso',
      { sso, sig },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  },

  /** Founder Console — read the founder's connected accounts + current source. */
  async getFounderConfig(token: string): Promise<FounderConfigResponse> {
    const res = await api.get('/boring-bucket/founder-config', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  /** Founder Console — set which connected account feeds Founder's Trades. */
  async setFounderAccount(token: string, connectionId: string): Promise<FounderConfigResponse> {
    const res = await api.post(
      '/boring-bucket/founder-config',
      { connectionId },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  },
};
